import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom, Subject } from "rxjs";
import { UserRow, UserDetail, UsersResponse, UserDetailResponse } from "allai-admin-shared";
import { environment } from '../../../environments/environment.js';
import { SnackbarService } from '../snackbar.service.js';
import { SessionService } from "../session.service";
import { PageLoaderService } from "../page-loader.service";
import { ActivatedRouteSnapshot } from '@angular/router';
import { UsersFilterService } from './user-filter.service.js';
import { VoiceMacro } from '@allaihealth/util';
import { VoiceMacroData } from '../../components/voice-macro-new-dialog/voice-macro-dialog.component.js';

/** Service that loads and caches all organizations in the organization list page */
@Injectable({
	providedIn: 'root'
})
export class UsersService {
	/** Loaded organizations by id */
	private _userList: UserRow[];
	private _total: number = 100;
	private _userDetail: UserDetail;

	private newUsersSubject = new Subject<void>();
	private newUserDetailSubject = new Subject<void>();

	constructor(private http: HttpClient,
		private sessionService: SessionService,
		private usersFilterService: UsersFilterService,
		private pageLoaderService: PageLoaderService,
		private snackbar: SnackbarService) {

		//clear things out if the user logs out
		this.sessionService.onLogout.subscribe(() => {
			this.clear();
		});

		//listen to filter changes
		this.usersFilterService.onNewFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolveUsers());
		});
		//listen to detail changes
		this.usersFilterService.onNewDetailFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolveUserDetail());
		});
	}

	/** Resolver to get all organizations for the list if not already loaded */
	static resolveUsers(): Promise<UserRow[]> {
		return inject(UsersService).resolveUsers();
	}

	/** Resolver to get the organization that is clicked on */
	static resolveUserDetail = async (route: ActivatedRouteSnapshot) => {
		return inject(UsersService).resolveUserDetail(route.params["id"]);
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolveUsers(): Promise<UserRow[]> {
		try {
			const params: HttpParams = this.usersFilterService.params;

			//load from server
			const response = await firstValueFrom(this.http.get<UsersResponse>(`${environment.apiUrl}/users`, { params }));

			this._userList = response.users;
			this._total = response.total;


			//dispatch that there was a change
			this.newUsersSubject.next();

		} catch (e) {
			//clear filter on error, just in case
			this.usersFilterService.clear();

			console.error("Failed to load users from server", e);
			this.snackbar.error("Failed to download users");
		}

		return this._userList;
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolveUserDetail(userId?: string): Promise<UserDetail> {

		try {
			const id = userId ?? this._userDetail.user.id;
			const params: HttpParams = this.usersFilterService.detailParams;
			const userResponse = await firstValueFrom(this.http.get<UserDetailResponse>(`${environment.apiUrl}/users/${id}`, { params }));

			this._userDetail = {
				user: userResponse.user,
				organization: userResponse.organization,
				metrics: userResponse.metrics,
				feedback: userResponse.feedback,
				voiceMacros: userResponse.voiceMacros

			}
			this.newUserDetailSubject.next();

		} catch (ignore) {
		}
		return this._userDetail;
	}

	get onNewUsers() {
		return this.newUsersSubject.asObservable();
	}

	get onNewUserDetail() {
		return this.newUserDetailSubject.asObservable();
	}

	/** Clear out loaded organizations and force a reload */
	clear() {
		this._userList = undefined;
		this._total = 100;
	}

	/** Get the sorted organizations */
	get userList() {
		return this._userList;
	}

	/** Get the organization detail */
	get userDetail() {
		return this._userDetail;
	}

	get total(): number {
		return this._total;
	}

	async newMacro(macro: VoiceMacroData) {
		const newMacro = await firstValueFrom(this.http.post<VoiceMacro>(`${environment.apiUrl}/voice-macro`,
			{
				macro,
				providerId: this.userDetail.user.id,
				organizationId: this.userDetail.user.id_organization
			}));
		this.userDetail.voiceMacros.push(newMacro);
	}

	async importMacros(macros: VoiceMacroData[]) {
		const newMacros = await firstValueFrom(this.http.post<VoiceMacro[]>(`${environment.apiUrl}/voice-macro/import`,
			{
				macros,
				providerId: this.userDetail.user.id,
				organizationId: this.userDetail.user.id_organization
			}));

		this.userDetail.voiceMacros.push(...newMacros);
	}

	async deleteMacro(macro: VoiceMacro) {
		await firstValueFrom(this.http.delete<any>(`${environment.apiUrl}/voice-macro/${macro.id}`));
		this.userDetail.voiceMacros.splice(this.userDetail.voiceMacros.indexOf(macro),1);
	}

	async updateMacro(macro: VoiceMacro, data: VoiceMacroData) {
		await firstValueFrom(this.http.put<any>(`${environment.apiUrl}/voice-macro/${macro.id}`,{
			phrase: data.phrase,
			section: data.section,
			substitution: data.substitution
		}));
		macro.phrase = data.phrase;
		macro.section = data.section;
		macro.substitution = data.substitution;
	}
}
